import { of, tap, merge, fromEvent, map, switchMap, BehaviorSubject, filter, finalize, distinctUntilChanged, EMPTY, combineLatest } from 'rxjs';

import { replaceViewContainer, renderTemplate } from './view.js';
import { jobsQueueLength$, submitJob, getJob } from './api.js';

export function createPromptView(forkedJobId=null) {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('prompt-view');
            replaceViewContainer(root);
            let submitting$ = new BehaviorSubject(false);
            let loading$ = new BehaviorSubject(false);
            let form = document.forms.promptForm;

            let loadForkedJob$ = of(undefined).pipe(
                switchMap(() => {
                    if (!forkedJobId) {
                        return EMPTY;
                    }
                    loading$.next(true);
                    let loadJob$ = getJob(forkedJobId).pipe(
                        tap(job => {
                            form.prompt.value = job.prompt;
                            form.negativePrompt.value = job.negative_prompt;
                            form.nsfw.checked = job.nsfw;
                            // TODO find best matching aspect ratio based on width and height
                            form.aspectRatio.value = '1:1';
                        }),
                        finalize(() => loading$.next(false)),
                    );
                    return merge(loadJob$);
                }),
            );

            let queueCounter$ = jobsQueueLength$.pipe(
                distinctUntilChanged(),
                tap(jobCount => root.querySelector('.queue-length').textContent = `${jobCount}`),
            );

            let submit$ = fromEvent(form, 'submit').pipe(
                map(event => {
                    event.preventDefault();
                    return {
                        prompt: (form.prompt.value || '').trim(),
                        negative_prompt: (form.negativePrompt.value || '').trim(),
                        aspect_ratio: form.aspectRatio.value,
                        nsfw: !!form.nsfw.checked,
                    };
                }),
                filter(job => job.prompt && job.aspect_ratio),
                switchMap(job => {
                    submitting$.next(true);
                    return submitJob(job).pipe(
                        tap(() => form.reset()),
                        finalize(() => submitting$.next(false)),
                    );
                }),
            );

            let inputsDisabled$ = combineLatest([submitting$, loading$]).pipe(
                map(([submitting, loading]) => submitting || loading),
                distinctUntilChanged(),
                tap(disabled => {
                    for (let el of root.querySelectorAll('textarea, select, button')) {
                        el.disabled = disabled;
                    }
                }),
            );

            return merge(
                loadForkedJob$,
                queueCounter$,
                submit$,
                inputsDisabled$,
            );
        }),
    );
}
